import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { extractApiErrorMessage } from '@/lib/api-error';
import { getFeatureFlags, updateFeatureFlags } from '@/services/api.client';
import type { FeatureFlag } from '@/types';

function FlagRow({ flag }: { flag: FeatureFlag }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => updateFeatureFlags({ flags: { [flag.moduleId]: enabled } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] }),
    // Previously silent per audit: Switch snapped back without feedback. Now
    // surface the reason so the super-admin knows the toggle didn't land.
    onError: (err) => {
      toast.error(`Failed to update ${flag.name}`, {
        description: extractApiErrorMessage(err, 'The toggle did not save. Please try again.'),
      });
    },
  });

  return (
    <div className="flex items-center justify-between py-3 border-b border-[var(--bg-surface-raised)] last:border-0">
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-medium text-[var(--text-primary)]">{flag.name}</p>
        <p className="text-xs text-[var(--text-secondary)]">{flag.description}</p>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5 font-mono">{flag.moduleId}</p>
      </div>
      <Switch
        checked={flag.defaultEnabled}
        disabled={mutation.isPending}
        onCheckedChange={(checked) => mutation.mutate(checked)}
        aria-label={`Toggle ${flag.name}`}
      />
    </div>
  );
}

export function SuperAdminFeatureFlagsPage() {
  const flagsQuery = useQuery({
    queryKey: ['admin', 'feature-flags'],
    queryFn: getFeatureFlags,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl text-[var(--text-primary)]">Feature Flags</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          Platform-wide module defaults applied to newly created properties
        </p>
      </div>

      <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] px-4">
        <QueryStateBoundary
          query={flagsQuery}
          errorTitle="Couldn't load feature flags"
          errorFallback="The feature-flag list is unavailable. Try again in a moment."
          loadingFallback={
            <>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-3 border-b border-[var(--bg-surface-raised)] last:border-0"
                >
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-6 w-10 rounded-full" />
                </div>
              ))}
            </>
          }
          emptyMessage="No feature flags configured."
        >
          {(flags) => (
            <>
              {flags.map((flag) => (
                <FlagRow key={flag.moduleId} flag={flag} />
              ))}
            </>
          )}
        </QueryStateBoundary>
      </div>
    </div>
  );
}
